import { NextSeo } from "next-seo";
import { BadgeCheck } from "lucide-react";

import RelatedPages from "@/components/marketing/RelatedPages";
import { SectionHeader } from "@/components/marketing/sections";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import JsonLd from "@/components/seo/JsonLd";
import AppStoreLink from "@/components/shared/AppStoreLink";
import FAQ, { type FaqItem } from "@/components/shared/FAQ";
import Footer from "@/components/shared/Footer";
import BasicBlock from "@/components/shared/basic-block";
import HeroBlock from "@/components/shared/hero-block";
import { APP_STORE_URL, IOS_APP } from "@/lib/app-store";
import { SITE_URL } from "@/lib/founders/content";
import { breadcrumbsForPath } from "@/lib/seo/breadcrumbs";
import { pageGraph } from "@/lib/seo/graph";
import { absoluteUrl, mobileApplication } from "@/lib/seo/schema";

const PATH = IOS_APP.path;
const CRUMBS = breadcrumbsForPath(PATH);

const SEO_TITLE = "Crayo iOS App: The Official Crayo App for iPhone";
const SEO_DESCRIPTION =
  "The official Crayo app for iPhone is Crayo: AI Video Editor, published by Crayo, Inc. What it does and how your crayo.ai account and plan carry over.";

const APP_DESCRIPTION =
  "The official Crayo iOS app. Auto clip long videos, add animated subtitles, generate story videos and voiceovers, and export vertical video for TikTok, Reels and Shorts. Signs in with your crayo.ai account.";

const FEATURES = [
  {
    title: "Auto Clip",
    body: "Upload a podcast, stream or interview. Crayo finds the moments, reframes them for vertical and hands you a grid of ready-to-post clips.",
  },
  {
    title: "Viral subtitles",
    body: "30+ animated caption styles, word-level timing you can edit by hand, keyword highlights and translation.",
  },
  {
    title: "Story videos",
    body: "Reddit-style and fake text videos from a link or a script, with AI voiceover and gameplay or stock backgrounds.",
  },
  {
    title: "AI tools",
    body: "Image and icon generation, voiceovers, voice changer, speech enhancer, subtitle and vocal removers, face swap.",
  },
  {
    title: "Full editor",
    body: "Multi-track timeline with trim, split, text presets, stock music, sound effects and VFX, in Simple or Advanced mode.",
  },
  {
    title: "Free tools on your phone",
    body: "Trim, compress, crop, convert, reverse and more, processed on the device. Nothing is uploaded and nothing is billed.",
  },
  {
    title: "View Tracker",
    body: "Follow channel growth and post performance across platforms from the same account you track on the web.",
  },
  {
    title: "Export and sync",
    body: "1080 by 1920 exports saved straight to Photos. Projects, assets and credits stay in sync with crayo.ai.",
  },
];

const ACCOUNT_FACTS = [
  {
    title: "Same plans, same prices",
    body: "Hobby, Clipper and Pro, billed monthly, quarterly or yearly, plus credit top-ups. Buy on the web or in the app and it works in both.",
  },
  {
    title: "Everything syncs",
    body: "Projects, assets and credits belong to one account. Start a clip on your phone and finish it in the web editor.",
  },
  {
    title: "Manage billing where you bought it",
    body: "Plans bought in the app are managed in your Apple subscriptions. Plans bought on crayo.ai are managed in Settings on the web.",
  },
];

const faqs: FaqItem[] = [
  {
    question: "Is there an official Crayo app?",
    answer: `Yes. ${IOS_APP.name} is the official Crayo app for iPhone and iPad, published on the App Store by ${IOS_APP.seller} It launched in October 2026 and requires ${IOS_APP.minimumOs} or later. It is free to download and you sign in with the account you use on crayo.ai.`,
  },
  {
    question: "Does my crayo.ai subscription work in the app?",
    answer:
      "Yes. Sign in with the same email or Apple or Google account and your Hobby, Clipper or Pro plan, your credits and your projects are already there. There is nothing to buy again.",
  },
  {
    question: "Is there a Crayo app for Android?",
    answer:
      "Not yet. Any Android listing that uses the Crayo name is not made by Crayo, Inc. Until an official Android app ships, crayo.ai works in the browser on Android phones.",
  },
  {
    question: "What does the app cost?",
    answer:
      "The download is free, and the on-device free tools cost nothing to use. Generating and exporting videos needs a Crayo plan, exactly as on the web: Hobby, Clipper or Pro. The prices and credits match crayo.ai.",
  },
];

const IosAppPage = () => (
  <div className="flex size-full flex-col bg-white">
    <NextSeo
      title={SEO_TITLE}
      description={SEO_DESCRIPTION}
      openGraph={{
        title: SEO_TITLE,
        description: SEO_DESCRIPTION,
        url: `${SITE_URL}${PATH}`,
        images: [
          { url: absoluteUrl(IOS_APP.ogImage), width: 1200, height: 630, alt: IOS_APP.name },
        ],
      }}
    />
    <JsonLd
      nodes={pageGraph({
        path: PATH,
        name: SEO_TITLE,
        description: SEO_DESCRIPTION,
        crumbs: CRUMBS,
        faqs,
        speakable: [".answer-first"],
        mainEntity: { "@id": `${SITE_URL}${PATH}#app` },
        extra: [
          mobileApplication({
            name: IOS_APP.name,
            url: PATH,
            installUrl: APP_STORE_URL,
            description: APP_DESCRIPTION,
            image: IOS_APP.icon,
            operatingSystem: "iOS 18.0 or later",
            datePublished: IOS_APP.releaseDate,
          }),
        ],
      })}
    />
    <Breadcrumbs items={CRUMBS} />

    <HeroBlock wrapperStyle="gap-6 py-10 md:py-14 lg:py-16">
      <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[#335CFF]">
        <BadgeCheck className="size-3.5" strokeWidth={2.25} aria-hidden />
        Official iOS app
      </span>
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:gap-10">
        <img
          src={IOS_APP.icon}
          alt={`${IOS_APP.name} app icon`}
          width={128}
          height={128}
          draggable={false}
          className="size-24 shrink-0 select-none rounded-[22%] shadow-md md:size-32"
        />
        <div className="flex flex-col gap-4">
          <h1 className="max-w-3xl text-[38px] font-semibold leading-[1.05] tracking-tight text-[#101828] md:text-[52px]">
            Crayo for iPhone
          </h1>
          <p className="answer-first max-w-3xl text-[16px] leading-[1.75] text-[#525866] md:text-[17px]">
            {IOS_APP.name} is the official Crayo app, published on the App Store by {IOS_APP.seller}
            , the company behind crayo.ai. It is free to download, you sign in with your crayo.ai
            account, and your projects, credits and plan are already there.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <AppStoreLink cta="ios-page-hero" height={56} />
        <p className="text-[13px] text-[#8B93A7]">
          Listed by {IOS_APP.seller} on the App Store, {IOS_APP.minimumOs} or later. Signs in with
          your crayo.ai account.
        </p>
      </div>
    </HeroBlock>

    <BasicBlock
      header={<SectionHeader>What you can do in the app</SectionHeader>}
      contentStyle="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-6"
    >
      {FEATURES.map((feature) => (
        <div
          key={feature.title}
          className="flex flex-col gap-2 rounded-2xl border border-[#EFF2FF] bg-[#F9FBFF] p-6"
        >
          <h3 className="text-base font-semibold text-[#101828]">{feature.title}</h3>
          <p className="text-sm leading-normal text-[#525866]">{feature.body}</p>
        </div>
      ))}
    </BasicBlock>

    <BasicBlock
      header={<SectionHeader>One account, on the web and on your phone</SectionHeader>}
      contentStyle="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6"
    >
      {ACCOUNT_FACTS.map((fact) => (
        <div
          key={fact.title}
          className="flex flex-col gap-2 rounded-2xl border border-[#EFF2FF] bg-white p-6 shadow-sm"
        >
          <h3 className="text-base font-semibold text-[#101828]">{fact.title}</h3>
          <p className="text-sm leading-normal text-[#525866]">{fact.body}</p>
        </div>
      ))}
    </BasicBlock>

    <FAQ items={faqs} />
    <RelatedPages path={PATH} />
    <Footer />
  </div>
);

export default IosAppPage;
